// src/components/GlobalSearch.jsx
// Persistent sidebar global search — searches customers, estimates, invoices, jobs
// All data comes from props (already loaded in CRM.jsx hooks — zero new Firestore reads)


import { useState, useRef, useEffect, useCallback } from 'react';


const RESULT_LIMIT = 4;


function highlight(text, query) {
 if (!text || !query) return text;
 const idx = text.toLowerCase().indexOf(query.toLowerCase());
 if (idx === -1) return text;
 return (
   <>
     {text.slice(0, idx)}
     <mark style={{ background: '#7f1d1d', color: '#fff', borderRadius: 2, padding: '0 2px' }}>
       {text.slice(idx, idx + query.length)}
     </mark>
     {text.slice(idx + query.length)}
   </>
 );
}


function matchesQuery(fields, query) {
 return fields.some(f => f && f.toLowerCase().includes(query.toLowerCase()));
}


export default function GlobalSearch({ customers, estimates, invoices, jobs, t, lang, onNavigate }) {
 const [query, setQuery] = useState('');
 const [open, setOpen] = useState(false);
 const [activeIdx, setActiveIdx] = useState(-1);
 const inputRef = useRef(null);
 const containerRef = useRef(null);
 const debounceRef = useRef(null);
 const [debouncedQuery, setDebouncedQuery] = useState('');


 // Debounce input 300ms
 useEffect(() => {
   clearTimeout(debounceRef.current);
   debounceRef.current = setTimeout(() => setDebouncedQuery(query), 300);
   return () => clearTimeout(debounceRef.current);
 }, [query]);


 // Close on outside click
 useEffect(() => {
   function handleClick(e) {
     if (containerRef.current && !containerRef.current.contains(e.target)) {
       setOpen(false);
       setActiveIdx(-1);
     }
   }
   document.addEventListener('mousedown', handleClick);
   return () => document.removeEventListener('mousedown', handleClick);
 }, []);


 // Keyboard shortcut: Cmd+K / Ctrl+K
 useEffect(() => {
   function handleKey(e) {
     if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
       e.preventDefault();
       inputRef.current?.focus();
       setOpen(true);
     }
     if (e.key === 'Escape') {
       setOpen(false);
       setQuery('');
       inputRef.current?.blur();
     }
   }
   document.addEventListener('keydown', handleKey);
   return () => document.removeEventListener('keydown', handleKey);
 }, []);


 const results = useCallback(() => {
   if (!debouncedQuery.trim() || debouncedQuery.length < 2) return [];


   const q = debouncedQuery.trim();
   const out = [];


   // Customers
   const matchedCustomers = (customers || [])
     .filter(c => matchesQuery([c.firstName, c.lastName, c.company, c.city, c.phone, c.email], q))
     .slice(0, RESULT_LIMIT)
     .map(c => ({
       type: 'customer',
       id: c.id,
       label: [c.firstName, c.lastName].filter(Boolean).join(' ') || c.company || c.email,
       sub: [c.company, c.city].filter(Boolean).join(' · '),
       raw: c,
     }));


   // Estimates
   const matchedEstimates = (estimates || [])
     .filter(e => matchesQuery([e.number, e.title, e.customerName,
       (customers || []).find(c => c.id === e.customerId)
         ? [(customers.find(c => c.id === e.customerId).firstName),
            (customers.find(c => c.id === e.customerId).lastName)].join(' ')
         : ''], q))
     .slice(0, RESULT_LIMIT)
     .map(e => {
       const cust = (customers || []).find(c => c.id === e.customerId);
       return {
         type: 'estimate',
         id: e.id,
         label: e.title || e.number,
         sub: [e.number, cust ? [cust.firstName, cust.lastName].filter(Boolean).join(' ') : null, e.status]
           .filter(Boolean).join(' · '),
         raw: e,
       };
     });


   // Invoices
   const matchedInvoices = (invoices || [])
     .filter(i => matchesQuery([i.number, i.title,
       (customers || []).find(c => c.id === i.customerId)
         ? [(customers.find(c => c.id === i.customerId).firstName),
            (customers.find(c => c.id === i.customerId).lastName)].join(' ')
         : ''], q))
     .slice(0, RESULT_LIMIT)
     .map(i => {
       const cust = (customers || []).find(c => c.id === i.customerId);
       return {
         type: 'invoice',
         id: i.id,
         label: i.title || i.number,
         sub: [i.number, cust ? [cust.firstName, cust.lastName].filter(Boolean).join(' ') : null, i.status]
           .filter(Boolean).join(' · '),
         raw: i,
       };
     });


   // Jobs
   const matchedJobs = (jobs || [])
     .filter(j => matchesQuery([j.title,
       (customers || []).find(c => c.id === j.customerId)
         ? [(customers.find(c => c.id === j.customerId).firstName),
            (customers.find(c => c.id === j.customerId).lastName)].join(' ')
         : '', j.status], q))
     .slice(0, RESULT_LIMIT)
     .map(j => {
       const cust = (customers || []).find(c => c.id === j.customerId);
       return {
         type: 'job',
         id: j.id,
         label: j.title,
         sub: [cust ? [cust.firstName, cust.lastName].filter(Boolean).join(' ') : null, j.status, j.startDate]
           .filter(Boolean).join(' · '),
         raw: j,
       };
     });


   if (matchedCustomers.length) out.push({ section: lang === 'fr' ? 'Clients' : 'Customers', items: matchedCustomers });
   if (matchedEstimates.length) out.push({ section: lang === 'fr' ? 'Soumissions' : 'Estimates', items: matchedEstimates });
   if (matchedInvoices.length) out.push({ section: lang === 'fr' ? 'Factures' : 'Invoices', items: matchedInvoices });
   if (matchedJobs.length) out.push({ section: lang === 'fr' ? 'Travaux' : 'Jobs', items: matchedJobs });


   return out;
 }, [debouncedQuery, customers, estimates, invoices, jobs, lang]);


 const allItems = results().flatMap(g => g.items);
 const grouped = results();
 const hasResults = grouped.length > 0;
 const noResults = debouncedQuery.length >= 2 && !hasResults;


 function handleSelect(item) {
   setOpen(false);
   setQuery('');
   setDebouncedQuery('');
   setActiveIdx(-1);
   onNavigate(item);
 }


 function handleKeyDown(e) {
   if (!open) return;
   if (e.key === 'ArrowDown') {
     e.preventDefault();
     setActiveIdx(i => Math.min(i + 1, allItems.length - 1));
   } else if (e.key === 'ArrowUp') {
     e.preventDefault();
     setActiveIdx(i => Math.max(i - 1, 0));
   } else if (e.key === 'Enter' && activeIdx >= 0) {
     e.preventDefault();
     handleSelect(allItems[activeIdx]);
   }
 }


 const typeIcon = {
   customer: '👤',
   estimate: '📋',
   invoice: '🧾',
   job: '🏗️',
 };


 let flatIdx = 0;


 return (
   <div ref={containerRef} style={{ position: 'relative', padding: '8px 12px 4px' }}>
     <div style={{
       display: 'flex', alignItems: 'center', gap: 6,
       background: '#2d0f0f', border: '1px solid #5a1010',
       borderRadius: 6, padding: '6px 10px',
     }}>
       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5"
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
         <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
       </svg>
       <input
         ref={inputRef}
         type="text"
         value={query}
         onChange={e => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
         onFocus={() => { if (query.length >= 2) setOpen(true); }}
         onKeyDown={handleKeyDown}
         placeholder={lang === 'fr' ? 'Rechercher… (⌘K)' : 'Search… (⌘K)'}
         style={{
           background: 'transparent', border: 'none', outline: 'none',
           color: '#f9fafb', fontSize: 13, width: '100%',
         }}
       />
       {query && (
         <button onClick={() => { setQuery(''); setDebouncedQuery(''); setOpen(false); }}
           style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, lineHeight: 1 }}>
           ✕
         </button>
       )}
     </div>


     {open && (hasResults || noResults) && (
       <div style={{
         position: 'absolute', top: '100%', left: 12, right: 12, zIndex: 1000,
         background: '#1f1f1f', border: '1px solid #5a1010', borderRadius: 8,
         boxShadow: '0 8px 32px rgba(0,0,0,0.6)', overflow: 'hidden',
         maxHeight: 420, overflowY: 'auto',
       }}>
         {noResults && (
           <div style={{ padding: '16px 12px', color: '#9ca3af', fontSize: 13, textAlign: 'center' }}>
             {lang === 'fr' ? 'Aucun résultat' : 'No results'}
           </div>
         )}
         {grouped.map(group => (
           <div key={group.section}>
             <div style={{
               padding: '8px 12px 4px', fontSize: 10, fontWeight: 700,
               color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em',
               borderTop: '1px solid #2d2d2d',
             }}>
               {group.section}
             </div>
             {group.items.map(item => {
               const thisIdx = flatIdx++;
               const isActive = thisIdx === activeIdx;
               return (
                 <div key={item.id}
                   onMouseDown={() => handleSelect(item)}
                   onMouseEnter={() => setActiveIdx(thisIdx)}
                   style={{
                     display: 'flex', alignItems: 'center', gap: 10,
                     padding: '8px 12px', cursor: 'pointer',
                     background: isActive ? '#3d1010' : 'transparent',
                     transition: 'background 0.1s',
                   }}
                 >
                   <span style={{ fontSize: 14, flexShrink: 0 }}>{typeIcon[item.type]}</span>
                   <div style={{ minWidth: 0 }}>
                     <div style={{ fontSize: 13, color: '#f9fafb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                       {highlight(item.label, debouncedQuery)}
                     </div>
                     {item.sub && (
                       <div style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                         {highlight(item.sub, debouncedQuery)}
                       </div>
                     )}
                   </div>
                 </div>
               );
             })}
           </div>
         ))}
         <div style={{ padding: '6px 12px', borderTop: '1px solid #2d2d2d', fontSize: 10, color: '#4b5563', display: 'flex', gap: 12 }}>
           <span>↑↓ {lang === 'fr' ? 'naviguer' : 'navigate'}</span>
           <span>↵ {lang === 'fr' ? 'ouvrir' : 'open'}</span>
           <span>Esc {lang === 'fr' ? 'fermer' : 'close'}</span>
         </div>
       </div>
     )}
   </div>
 );
}
